import React from 'react';
import PageLayout from '@components/PageLayout';
import styles from './CommunityPage.module.css';

const CommunityPage: React.FC = () => {
  const communityCards = [
    {
      id: 1,
      title: 'Forums and Topics',
      description: 'Find everything you need to get around: forums, announcements and gaming gigs.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      ),
      buttonText: 'View Topics',
      link: 'https://community.commvault.com/',
    },
    {
      id: 2,
      title: 'Ask the experts',
      description: 'Get help from your peers and Commvault experts, or share your insights.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
          <path d="M2 17l10 5 10-5"></path>
          <path d="M2 12l10 5 10-5"></path>
        </svg>
      ),
      buttonText: 'Create a Post',
      link: 'https://community.commvault.com/',
    },
    {
      id: 3,
      title: 'News & Events',
      description: 'Stay updated with the latest product updates, technical insights, and community news.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="16" rx="2"></rect>
          <line x1="7" y1="8" x2="17" y2="8"></line>
          <line x1="7" y1="12" x2="17" y2="12"></line>
          <line x1="7" y1="16" x2="13" y2="16"></line>
        </svg>
      ),
      buttonText: 'Read More',
      link: 'https://community.commvault.com/',
    },
  ];

  const handleActionClick = (link: string) => {
    window.open(link, '_blank');
  };

  const benefits = [
    {
      title: 'Peer driven problem solving:',
      description: 'Many common questions or issues can be resolved via community forums before needing formal support.',
    },
    {
      title: 'Knowledge sharing:',
      description: 'Community members share better practices, real world experience, and tips that go beyond formal documentation.',
    },
    {
      title: 'User engagement & loyalty:',
      description: 'Provides a venue for customers and partners to contribute, which strengthens their investment in the product ecosystem.',
    },
    {
      title: 'Amplifies product feedback:',
      description: 'Community discussion can help Commvault surface pain points, feature requests, and area improvements directly from its users.',
    },
    {
      title: 'Scalable support resource:',
      description: 'With a vibrant community, some support burden is alleviated by peer support and self-help.',
    },
  ];

  return (
    <PageLayout>
      <div className={styles.unifiedContainer}>
        {/* Community Info */}
        <section className={styles.communitySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.communityIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            Community
          </h2>
          <p className={styles.subtitle}>
            Welcome to the Commvault Cloud Community and a New Era of Cyber Resilience
          </p>
          <ul className={styles.introList}>
            <li>
              The Commvault Community is a public, gated forum and discussion hub for customers, partners, and Commvault experts to share knowledge, discuss industries, and engage with teams.
            </li>
            <li>
              It's part of Commvault's broader customer engagement strategy: a "live-first" hub for product updates, technical insights, and community-driven content.
            </li>
          </ul>
          <div className={styles.smallLine}></div>
        </section>

        {/* Community Cards Grid */}
        <div className={styles.categoryGrid}>
          {communityCards.map((card) => (
            <div key={card.id} className={styles.categoryCard}>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              <div className={styles.cardHeaderLine}></div>
              <div className={styles.cardIconWrapper}>
                <div className={styles.squareIcon}>
                  <div className={styles.iconOuter}></div>
                  <div className={styles.iconInner}>
                    <div className={styles.cardIcon}>{card.icon}</div>
                  </div>
                </div>
              </div>
              <p className={styles.cardDescription}>{card.description}</p>
              <button
                className={styles.cardButton}
                onClick={() => handleActionClick(card.link)}
              >
                {card.buttonText}
              </button>
            </div>
          ))}
        </div>

        {/* Value & Benefits */}
        <section className={styles.newsSection}>
          <h2 className={styles.newsTitle}>Value & Benefits</h2>
          <ul className={styles.newsList}>
            {benefits.map((item, index) => (
              <li key={index}>
                <strong>{item.title}</strong> {item.description}
              </li>
            ))}
          </ul>
        </section>

        {/* Launch Button */}
        <div className={styles.launchSection}>
          <button
            className={styles.launchButton}
            onClick={() => handleActionClick('https://community.commvault.com/')}
          >
            Launch Community Discussions
          </button>
        </div>
      </div>
    </PageLayout>
  );
};

export default CommunityPage;
